/**
 * ACT2-1108: "Connect your warehouse". Lists the warehouse accounts Lookout can connect, each with the same
 * one-click Connect as Settings › Warehouses and chat's connect card, and opens the Add warehouse drawer for
 * a new one. The list is `useWarehouseAccountList`, the list both of those use: in a sibling workspace
 * (ACT2-1124) it holds the accounts of the readable workspaces too, "From <workspace>", whose Connect copies
 * the account first. Once an account is connected it is listed above them, and the step stays open so
 * another can be connected.
 *
 * `focusAccountId` (an in-chat assist card) narrows the list to that one account, with "Show all" for the
 * rest, and leaves out the connected list: the card itself reports the connection. Without it the step
 * is the panel's, unchanged.
 */
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Plus } from 'lucide-react';
import { useState } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { ConnectableAccountRow } from '@/chat/components/onboarding/ConnectWarehouseCard';
import { CopyAccountDialog } from '@/settings/components/accountCopies/CopyAccountDialog';
import { ReadableWorkspacesNotice } from '@/settings/components/accountCopies/ReadableWorkspacesNotice';
import { useWarehousePermission } from '@/settings/components/warehouseAccount/accountCreatePermission';
import { AddWarehouseAccountDrawer } from '@/settings/components/warehouseAccount/addAccount/AddWarehouseAccountDrawer';
import { PermissionNotice } from '@/settings/components/warehouseAccount/PermissionNotice';
import { useWarehouseAccountList } from '@/settings/components/warehouseAccount/useWarehouseAccountList';
import { warehouseTypeMeta } from '@/settings/components/warehouseAccount/warehouseTypes';

interface WarehouseStepProps {
  /** A RudderStack account id to list alone, ahead of a "Show all" for the others. */
  focusAccountId?: string;
}

export function WarehouseStep({ focusAccountId }: WarehouseStepProps = {}): React.ReactElement {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // ACT2-1276: the RudderStack permission to create warehouse accounts decides connecting here, as it does
  // in Settings › Warehouses and chat's connect card. The Lookout admin role does not.
  const permission = useWarehousePermission();
  const canConnect = permission.blocked === null;
  // A connection completes the step: re-read the setup status rather than wait for its next poll.
  const refreshStatus = (): void =>
    void queryClient.invalidateQueries({ queryKey: orpc.setup.status.queryOptions().queryKey });

  // A connect from an account of a readable workspace (copy, then connect) ends in `onAdoptedAsNew` too.
  const list = useWarehouseAccountList({
    onAdoptedAsNew: refreshStatus,
    onConnectionSaved: () => {
      setAddOpen(false);
      refreshStatus();
    },
  });
  const { adopt: hook, connections, organization, connectable, unconnectableCount, isLoading } = list;
  const unavailable = hook.unavailable !== undefined || hook.unavailableProblem !== undefined;

  // An account that is no longer connectable (connected since, or never eligible) leaves nothing to
  // focus on, so the whole list shows.
  const focused = focusAccountId === undefined ? [] : connectable.filter((a) => a.id === focusAccountId);
  const narrowed = focused.length > 0 && !showAll;
  const listed = narrowed ? focused : connectable;
  const connected = focusAccountId === undefined ? connections.accounts : [];

  return (
    <div className="flex flex-col gap-2">
      <PermissionNotice permission={permission} />
      {list.showReadableWorkspacesNotice && <ReadableWorkspacesNotice />}
      {organization.unavailable !== null && (
        <Callout variant="warning">
          Could not read the accounts of your organization’s other workspaces just now, so only this workspace’s
          accounts are listed.
        </Callout>
      )}
      {connected.length > 0 && (
        <ul aria-label="Connected warehouse accounts" className="flex flex-col gap-1">
          {connected.map((account) => {
            const { Icon, label } = warehouseTypeMeta(account.type);
            return (
              <li key={account.id} className="flex items-center gap-2 py-1">
                {/* Decorative: the account name and type are in the text beside it. */}
                <span aria-hidden className="flex shrink-0 items-center">
                  <Icon className="size-5" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <TruncateWithTooltip className="text-sm font-medium text-foreground">
                    {account.name}
                  </TruncateWithTooltip>
                  <span className="text-xs text-muted-foreground">{label}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-success">
                  <CircleCheck aria-hidden className="size-3.5" />
                  Connected
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {isLoading ? (
        <div className="flex flex-col gap-2" role="status" aria-label="Loading warehouse accounts">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : (
        <>
          {/* A failed read of this workspace's own accounts still lists the readable workspaces' accounts. */}
          {unavailable &&
            (hook.unavailableProblem ? (
              <WarehouseProblemCallout problem={hook.unavailableProblem} />
            ) : (
              <Callout variant="warning">Your RudderStack warehouse accounts couldn’t be loaded.</Callout>
            ))}
          {connectable.length > 0 ? (
            <ul aria-label="Warehouse accounts you can connect" className="-mx-2 flex flex-col">
              {listed.map((account) => (
                <ConnectableAccountRow
                  key={account.id}
                  account={account}
                  canConnect={canConnect}
                  compact
                  anyConnecting={list.anyConnecting}
                  onConnect={() => list.connect(account.id)}
                />
              ))}
            </ul>
          ) : unavailable ? null : (
            <p className="text-sm text-muted-foreground">
              {connected.length > 0
                ? 'Nothing else to connect.'
                : unconnectableCount > 0
                  ? // The same words as chat's connect card: the accounts exist, Lookout cannot connect them yet.
                    'None of your RudderStack warehouse accounts can be connected from Lookout yet.'
                  : 'No RudderStack warehouse accounts yet.'}
            </p>
          )}
        </>
      )}
      {narrowed && connectable.length > 1 && (
        <Button
          type="button"
          variant="text"
          className="self-start text-xs text-primary"
          label={`Show all ${connectable.length} accounts`}
          onClick={() => setShowAll(true)}
        />
      )}

      <ActionTooltip show={!canConnect && !permission.loading} message={permission.blocked ?? ''}>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          className="-ml-2 self-start"
          Icon={<Plus />}
          label={connected.length > 0 ? 'Set up another' : 'Set up a new warehouse'}
          disabled={!canConnect}
          onClick={() => setAddOpen(true)}
        />
      </ActionTooltip>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="text"
            className="self-start text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            label="What access does Lookout need?"
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="max-w-xs text-sm">
          Lookout runs read-only queries, so the account’s user needs read access to the data you want to ask about. To
          activate audiences, it also needs write access to the schema Reverse ETL writes its sync tables to.
        </PopoverContent>
      </Popover>

      <CopyAccountDialog organization={organization} />
      <AddWarehouseAccountDrawer
        open={addOpen}
        onOpenChange={setAddOpen}
        onConnected={refreshStatus}
        blockedReason={permission.blocked}
        hook={connections}
      />
    </div>
  );
}
